import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  LinearProgress,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
} from '@mui/material';
import dayjs from 'dayjs';
import { LoadingWrapper } from '../loading';
import { LightTooltip } from '../custom-components/custom-tooltip';

const FinanceIndicators = ({ recentTransactions, isLoading }) => {
  // Key Performance Indicators
  const kpiData = [
    {
      label: 'Revenue Growth',
      value: '12.5%',
      target: '10%',
      progress: 125,
      status: 'excellent',
    },
    {
      label: 'Profit Margin',
      value: '24.5%',
      target: '20%',
      progress: 122.5,
      status: 'excellent',
    },
    {
      label: 'Cost Efficiency',
      value: '75.5%',
      target: '80%',
      progress: 94.4,
      status: 'good',
    },
    {
      label: 'Cash Conversion',
      value: '45 days',
      target: '30 days',
      progress: 66.7,
      status: 'warning',
    },
  ];

  const getKpiStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return '#0A9EF2';
      case 'good':
        return '#5E69F4';
      case 'warning':
        return '#FF9800';
      case 'poor':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  return (
    <LoadingWrapper
      isLoading={isLoading}
      loadingType="skeleton"
      skeletonCount={3}
      gridProps={{ xs: 12, md: 4 }}
      containerSx={{ marginTop: 3 }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{ borderRadius: 2, height: '100%', boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1] }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Key Performance Indicators
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4}>
                Performance metrics against quarterly targets
              </Typography>

              <Stack spacing={4}>
                {kpiData.map((kpi, index) => (
                  <Box key={index}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {kpi.label}
                      </Typography>
                      <LightTooltip title={`Target: ${kpi.target}`}>
                        <Typography variant="h6" sx={{ color: getKpiStatusColor(kpi.status), fontWeight: 700 }}>
                          {kpi.value}
                        </Typography>
                      </LightTooltip>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={Math.min(kpi.progress, 100)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: () => alpha(getKpiStatusColor(kpi.status), 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: getKpiStatusColor(kpi.status),
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{ borderRadius: 2, height: '100%', boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1] }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Recent Transactions
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4}>
                Latest financial activities and settlements
              </Typography>

              <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Description</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Amount
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.disabled">
                            No recent transactions
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentTransactions.slice(0, 5).map((transaction) => (
                        <TableRow key={transaction.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', noWrap: true }}
                            >
                              {transaction.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(transaction.date).format('MMM DD, YYYY')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: transaction.type === 'income' ? 'success.main' : 'error.main',
                                fontWeight: 700,
                              }}
                            >
                              {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={transaction.status}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                backgroundColor: (theme) =>
                                  transaction.status === 'completed'
                                    ? alpha(theme.palette.success.main, 0.12)
                                    : alpha(theme.palette.warning.main, 0.12),
                                color: (theme) =>
                                  transaction.status === 'completed'
                                    ? theme.palette.success.main
                                    : theme.palette.warning.main,
                                border: (theme) =>
                                  `1px solid ${alpha(
                                    transaction.status === 'completed'
                                      ? theme.palette.success.main
                                      : theme.palette.warning.main,
                                    0.24
                                  )}`,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default FinanceIndicators;
